import { useMemo } from 'react';
import { BROWSER_TZ } from '@/lib/timezone';
import { DateInput, TimeInput } from './DateTime';
import { duePicks, type Due } from './due';
import { Icon } from './Icon';

// When a follow-up is due: quick picks for the usual days, a day box and an optional time. The follow-up dialog shows
// it full size; the record's composer uses the compact form in its row of options.

export function DueInput({
  value,
  onChange,
  id,
  compact,
  invalid,
  describedBy,
}: {
  value: Due;
  onChange: (next: Due) => void;
  /** Id of the day box, for a label. */
  id?: string;
  /** The 30px size for compact rows. */
  compact?: boolean;
  invalid?: boolean;
  describedBy?: string;
}) {
  const picks = useMemo(() => duePicks(new Date(), BROWSER_TZ), []);
  const on = picks.find((p) => p.dueAt === value.date)?.id;
  const box = compact ? 'input input-sm' : 'input';
  return (
    <div className={compact ? 'due sm' : 'due'}>
      <div className="due-picks" role="group" aria-label="Quick dates">
        {picks.map((p) => (
          <button key={p.id} type="button" className="due-pick" aria-pressed={p.id === on} title={p.sub} onClick={() => onChange({ ...value, date: p.dueAt })}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="due-when">
        <DateInput
          id={id}
          className={box}
          wrapClassName="due-date"
          value={value.date}
          required
          aria-label="Due date"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(date) => onChange({ ...value, date })}
        />
        <TimeInput
          className={box}
          wrapClassName="due-time"
          value={value.time}
          step={15}
          aria-label="Due time (optional)"
          aria-describedby={describedBy}
          onChange={(time) => onChange({ ...value, time })}
        />
        {value.time ? (
          <button type="button" className={compact ? 'icon-btn xs' : 'icon-btn sm'} aria-label="Remove the time" data-tip="Whole day" onClick={() => onChange({ ...value, time: '' })}>
            <Icon name="x" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
